import { ChangeEvent, useRef, useState } from "react";
import { Loader2, Paperclip } from "lucide-react";
import toast from "react-hot-toast";

import { classNames } from "utils";
import useApi from "utils/use-api";
import { useCurrentUser } from "_graphql/cache/auth";

const MAX_FILE_SIZE = 1024 * 1024 * 26; // 26MB
const ALLOWED_EXTENSIONS = [
    "pdf",
    "doc",
    "docx",
    "xls",
    "xlsx",
    "png",
    "jpg",
    "jpeg",
];
const ACCEPT_ATTR =
    ".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,application/pdf,image/*";

type Props = {
    offerId?: string;
    publishedParticipantId?: string;
    /** Called after a file is attached successfully (e.g. to refresh a list). */
    onUploaded?: () => void;
    className?: string;
    label?: string;
    /** Render only the paperclip icon (and a spinner while uploading). */
    showAsIcon?: boolean;
};

const AttachFileButton = ({
    offerId,
    publishedParticipantId,
    onUploaded,
    className,
    label = "Attach File",
    showAsIcon = false,
}: Props) => {
    const api = useApi();
    const { employee } = useCurrentUser();
    const inputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);

    const validateFile = (file: File) => {
        const ext = file.name.split(".").pop()?.toLowerCase() || "";
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            toast.error(
                "Unsupported file type. Upload a PDF, Office doc or image.",
            );
            return false;
        }
        if (file.size > MAX_FILE_SIZE) {
            toast.error("File is too large. Maximum size is 26MB.");
            return false;
        }
        return true;
    };

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // Reset early so re-selecting the same file still fires onChange.
        if (inputRef.current) inputRef.current.value = "";
        if (!file) return;

        if (!offerId || !publishedParticipantId) {
            toast.error("Missing participant information for upload.");
            return;
        }
        if (!employee?.employee_id) {
            toast.error("Missing employee information for upload.");
            return;
        }
        if (!validateFile(file)) return;

        const formData = new FormData();
        formData.append("file", file);
        formData.append("offer_id", offerId);
        formData.append(
            "offer_published_participant_id",
            publishedParticipantId,
        );
        formData.append("employee_id", employee.employee_id);

        setUploading(true);
        try {
            await api.post("/api/offer-participant-document/upload", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("File attached successfully");
            onUploaded?.();
        } catch (error) {
            toast.error("Failed to attach file");
        } finally {
            setUploading(false);
        }
    };

    return (
        <>
            <input
                ref={inputRef}
                type="file"
                accept={ACCEPT_ATTR}
                className="hidden"
                onChange={handleFileChange}
            />
            <button
                type="button"
                title={
                    showAsIcon
                        ? uploading
                            ? "Uploading..."
                            : label
                        : undefined
                }
                disabled={uploading || !publishedParticipantId}
                onClick={() => inputRef.current?.click()}
                className={classNames(
                    showAsIcon
                        ? "inline-flex items-center justify-center flex-shrink-0 p-3 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                        : "inline-flex items-center gap-1 flex-shrink-0 rounded-md border border-transparent shadow-sm px-3 py-2 bg-primary-600 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-60",
                    className || "",
                )}>
                {showAsIcon ? (
                    uploading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                        <Paperclip className="w-5 h-5" />
                    )
                ) : (
                    <>
                        <Paperclip className="w-4 h-4" />
                        {uploading ? "Uploading..." : label}
                    </>
                )}
            </button>
        </>
    );
};

export default AttachFileButton;
